import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { motion } from 'framer-motion';
import { supabase } from '../../../lib/supabase';


const RevenueChart = ({ orgId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('revenue'); // 'revenue' | 'jobs'

  useEffect(() => {
    if (!orgId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const months = [];
        const now = new Date();
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          months?.push({
            year: d?.getFullYear(),
            month: d?.getMonth(),
            label: d?.toLocaleString('default', { month: 'short' }),
            start: new Date(d.getFullYear(), d.getMonth(), 1)?.toISOString(),
            end: new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59)?.toISOString(),
          });
        }

        const results = await Promise.all(
          months?.map(m =>
            supabase?.from('jobs')?.select('total_cost, value, status')?.eq('org_id', orgId)?.is('deleted_at', null)?.gte('created_at', m?.start)?.lte('created_at', m?.end)
          )
        );

        const chartData = months?.map((m, i) => {
          const jobs = results?.[i]?.data || [];
          const revenue = jobs?.reduce((sum, j) => sum + (Number(j?.total_cost) || Number(j?.value) || 0), 0);
          const completed = jobs?.filter(j => j?.status === 'completed')?.length;
          const total = jobs?.length;
          return { month: m?.label, revenue, jobs: total, completed };
        });

        setData(chartData);
      } catch (err) {
        console.error('Revenue chart error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-popover border border-border rounded-xl p-3 shadow-lg">
        <p className="text-xs font-semibold text-muted-foreground mb-2">{label}</p>
        {view === 'revenue' ? (
          <p className="text-sm font-bold text-foreground">${Number(payload?.[0]?.value || 0)?.toLocaleString()}</p>
        ) : (
          <div className="space-y-1">
            <p className="text-sm font-bold text-foreground">{payload?.[0]?.value} total jobs</p>
            <p className="text-xs text-green-500">{payload?.[1]?.value} completed</p>
          </div>
        )}
      </div>
    );
  };

  const totalRevenue = data?.reduce((s, d) => s + d?.revenue, 0);
  const totalJobs = data?.reduce((s, d) => s + d?.jobs, 0);

  return (
    <motion.div
      className="bg-card border border-border rounded-2xl p-5 shadow-sm"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="text-base font-semibold text-foreground">Performance Trend</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Last 6 months</p>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          <button
            onClick={() => setView('revenue')}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${view === 'revenue' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Revenue
          </button>
          <button
            onClick={() => setView('jobs')}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${view === 'jobs' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Jobs
          </button>
        </div>
      </div>
      <div className="flex gap-6 mb-5">
        <div>
          <p className="text-2xl font-bold text-foreground">${totalRevenue?.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Total revenue</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">{totalJobs}</p>
          <p className="text-xs text-muted-foreground">Total jobs</p>
        </div>
      </div>
      {loading ? (
        <div className="h-52 bg-muted/40 rounded-xl animate-pulse" />
      ) : (
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            {view === 'revenue' ? (
              <AreaChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `$${(v / 1000)?.toFixed(0)}k` : `$${v}`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="revenue" stroke="var(--color-primary)" strokeWidth={2.5} fill="url(#revenueGrad)" dot={{ fill: 'var(--color-primary)', r: 3, strokeWidth: 0 }} activeDot={{ r: 5 }} />
              </AreaChart>
            ) : (
              <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="jobs" fill="var(--color-primary)" radius={[4, 4, 0, 0]} opacity={0.4} />
                <Bar dataKey="completed" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  );
};

export default RevenueChart;
